import { Flex, TextButton } from '@bamboohr/fabric';
import { isKnownPasskeyUser, isPasskeySupported } from '@utils/authentication';
import React, { useCallback, useMemo, useState } from 'react';
import { getRedirect } from '../../multi-factor-auth/domain';
import { EmailPasswordForm } from './email-password-form';
import { PasskeyLoginButton } from './passkey-login-button';
import { SsoButton } from './sso-button';
import { TextDivider } from './text-divider';

// LoginFormVars indicates the structure of some expected twigvars
export interface LoginFormVars {
	allowPasswordLogin: boolean;
	loginFormSubmitUri: string | null;
	ssoProvider: string;
}

export const LoginForm: React.FC<
	LoginFormVars & { onClickAlternateLoginMethod: () => void }
> = ({
	allowPasswordLogin,
	loginFormSubmitUri,
	ssoProvider,
	onClickAlternateLoginMethod,
}) => {
	// defining util functions as consts to avoid unnecessary function calls
	const isWebAuthnSupported = isPasskeySupported();
	const isKnownUser = isKnownPasskeyUser();

	const [showAlternateLogin, setShowAlternateLogin] = useState(false);

	const redirect = useMemo(() => {
		return getRedirect();
	}, []);
	const finalUri = useMemo(() => {
		return `${loginFormSubmitUri}?r=${encodeURIComponent(redirect)}`;
	}, [loginFormSubmitUri, redirect]);
	const buttonRedirectUri = useMemo(() => {
		return redirect ? `?r=${redirect}` : '';
	}, [redirect]);

	const hasSso = Boolean(ssoProvider);

	const offerPasskeyLogin = useMemo(
		() => isWebAuthnSupported && !isKnownUser && allowPasswordLogin,
		[isWebAuthnSupported, isKnownUser, allowPasswordLogin],
	);
	const prioritizePasskeyLogin = useMemo(
		() => isWebAuthnSupported && isKnownUser && allowPasswordLogin,
		[isWebAuthnSupported, isKnownUser, allowPasswordLogin],
	);

	const handleAlternateLoginMethod = useCallback(() => {
		setShowAlternateLogin(true);
		onClickAlternateLoginMethod();
	}, [onClickAlternateLoginMethod]);

	const renderNormalLoginLink = (
		labelMode: 'passkeySupported' | 'ssoAware',
	) => {
		if (!allowPasswordLogin) {
			return null;
		}

		let label: string;
		if (labelMode === 'passkeySupported') {
			label = isWebAuthnSupported
				? $.__('Log in another way')
				: $.__('Log in with Email and Password');
		} else {
			label = hasSso
				? $.__('Log in another way')
				: $.__('Log in with Email and Password');
		}

		return (
			<div className="normal-login-link-container">
				<TextButton onClick={handleAlternateLoginMethod} type="button">
					{label}
				</TextButton>
			</div>
		);
	};

	const getOfferPasskeyLoginContent = () => {
		return (
			<>
				{ssoProvider ? (
					<>
						<Flex flexDirection="column" gap={2}>
							<SsoButton
								buttonRedirectUri={buttonRedirectUri}
								ssoProvider={ssoProvider}
							/>
							{showAlternateLogin ? (
								<PasskeyLoginButton isKnownUser={isKnownUser} />
							) : null}
						</Flex>
						<TextDivider text="OR" />
						{renderNormalLoginLink('passkeySupported')}
					</>
				) : null}
				<EmailPasswordForm redirect={redirect} />
				{!ssoProvider ? (
					<>
						<TextDivider text="OR" />
						<PasskeyLoginButton isKnownUser={isKnownUser} />
					</>
				) : null}
			</>
		);
	};

	const getPrioritizePasskeyLoginContent = () => {
		return (
			<>
				<Flex flexDirection="column" gap={2}>
					<PasskeyLoginButton isKnownUser={isKnownUser} />
					{showAlternateLogin ? (
						<SsoButton
							buttonRedirectUri={buttonRedirectUri}
							ssoProvider={ssoProvider}
						/>
					) : null}
				</Flex>
				<TextDivider text="OR" />
				{renderNormalLoginLink('ssoAware')}
				<EmailPasswordForm redirect={redirect} />
			</>
		);
	};

	const getDefaultLoginContent = () => {
		return (
			<>
				{ssoProvider ? (
					<>
						<SsoButton
							buttonRedirectUri={buttonRedirectUri}
							ssoProvider={ssoProvider}
						/>
						<TextDivider text="OR" />
						{renderNormalLoginLink('passkeySupported')}
					</>
				) : null}
				<EmailPasswordForm redirect={redirect} />
			</>
		);
	};

	const getFormContent = () => {
		if (offerPasskeyLogin) {
			return getOfferPasskeyLoginContent();
		}
		if (prioritizePasskeyLogin) {
			return getPrioritizePasskeyLoginContent();
		}
		return getDefaultLoginContent();
	};

	const formClassName = useMemo(() => {
		const classes: string[] = ['BhrForms'];
		if (hasSso) {
			classes.push('sso-login');
		}
		if (offerPasskeyLogin) {
			classes.push('offer-passkey');
		}
		if (prioritizePasskeyLogin) {
			classes.push('prioritize-passkey');
		}
		return classes.join(' ');
	}, [hasSso, offerPasskeyLogin, prioritizePasskeyLogin]);

	return (
		<form
			action={finalUri}
			className={formClassName}
			method="post"
			name="loginform"
		>
			{getFormContent()}
		</form>
	);
};
